/**
 * The widget itself: Shadow-DOM mount, floating bubble + panel (or inline
 * container), chat log with delta streaming, UISpec renderers composing
 * canonical plain-text answers, and voice mode (push-to-talk via the server
 * voice gateway).
 */
import { ApiClient, ApiError } from "./api";
import {
  composeMultiSelect,
  composeCardSortState,
  composeRanking,
  PNTA_TEXT,
} from "./composer";
import { doneCopy, isClosedReason, splitInvite } from "./done";
import { Emitter } from "./events";
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  findLanguage,
  translate,
  type Language,
} from "./i18n";
import { bubbleText } from "./prompts";
import {
  clearSession,
  loadCompletion,
  loadSession,
  persistCompletion,
  saveSession,
} from "./session";
import { WIDGET_CSS } from "./styles";
import { brandThemeCss, resolveScheme, safeFontsHref } from "./theme";
import type {
  DeuteroInterviewConfig,
  DeuteroInterviewInstance,
  InterviewerReply,
  InterviewSnapshot,
  QuestionView,
  StateEvent,
  StreamEvent,
  StudyWelcome,
  SurveyBranding,
  UISpec,
  UtteranceEvent,
  WidgetEvents,
} from "./types";
import { buildUISpec } from "./uispec";
import { createMasker, loadPiiEngine } from "./pii";
import { buildContactForm, isContactView } from "./contact";
import type { ContactResult, ContactValues } from "./contact";
import { VoiceSession, type SessionRecording } from "./voice";
import { Recorder, type RecordingSession } from "./recorder";
import { PlayoutLog, type PlayoutResult } from "./playout";
import { workletUrl } from "./worklets";
import {
  indicatorText,
  mountRecordingNotice,
  noticeModel,
  type Draft,
  type NoticeModel,
  type Effective,
} from "./recordingNotice";
import { setRichText } from "./richtext";

const ICONS = {
  chat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
  send: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/></svg>',
  mic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>',
  speaker: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8.5a4.5 4.5 0 0 1 0 7M18.5 6a8 8 0 0 1 0 12"/></svg>',
  stop: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/></svg>',
};

// How long the finished thread stays readable before the ending splash covers
// it. Only for a completion we watch happen live — a visitor arriving at an
// interview that was already over has no closing message left to read.
const DONE_SPLASH_DELAY_MS = 60_000;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = "",
  text = ""
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/** The welcome turn as a resumed snapshot carries it: its own turn when the
 *  transcript still names one, else the last thing the interviewer said (a
 *  consent re-prompt, when the previous reply didn't resolve). The latest
 *  welcome, when a language switch said it more than once. */
function welcomeTurnText(snapshot: InterviewSnapshot): string {
  const said = (snapshot.transcript ?? []).filter((t) => t.role !== "participant");
  const welcome = said.filter((t) => t.tool_name === "welcome").pop();
  return (welcome ?? said[said.length - 1])?.text ?? "";
}

/** What the recording notice settled before the interview existed. */
interface RecordingStep {
  declined: boolean;
  /** Nothing further to ask or store once the interview exists. */
  settled: boolean;
  choice?: Draft;
}

/** How long a completed interview's recording waits for the closing line to
 *  finish playing before it ends anyway. */
const RECORDING_FINISH_WAIT_MS = 60_000;

export class DeuteroWidget implements DeuteroInterviewInstance {
  private emitter = new Emitter<WidgetEvents>();
  private api: ApiClient;
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private root!: HTMLElement;
  private panel!: HTMLElement;
  private bubble: HTMLButtonElement | null = null;
  private log!: HTMLElement;
  private choices!: HTMLElement;
  private status!: HTMLElement;
  private recBar: HTMLElement | null = null;
  /** Continuous session recording (src/recorder.ts): voice mode with
   *  effective audio on. The interviewer plays into `tap` (before `out`, the
   *  widget's output gain, so anything muted there is still recorded); each
   *  open mic is connected into `micGate`. Null when nothing is recorded. */
  private rec: {
    ctx: AudioContext;
    tap: GainNode;
    out: GainNode;
    micGate: GainNode;
    recorder: Recorder;
    /** Where each interviewer message was heard, for the word timings. */
    playout: PlayoutLog<RecordingSession>;
  } | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  /** The voice socket's open mic, kept while the panel is closed so it can
   *  be reconnected to the recorder on reopen. */
  private micStream: MediaStream | null = null;
  /** No live recording session right now (idle, or not started yet). */
  private recPaused = true;
  /** Recording was promised but isn't happening: the consent could not be
   *  stored, the browser can't record, or the recorder failed. */
  private recBroken = false;
  /** The broken state came only from recorder errors that may pass (a
   *  session that failed to create, an upload that gave up): the next
   *  session the server creates clears it. */
  private recBrokenTransient = false;
  private recFinishing = false;
  private tokenRefresh: Promise<void> | null = null;
  private progressEl!: HTMLElement;
  private input!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private micBtn: HTMLButtonElement | null = null;
  /** Hands-free: the participant turned the mic off, so a later
   *  `turn_taking: vad` frame must not reopen it behind their back. */
  private micMuted = false;
  private typingEl: HTMLElement | null = null;
  // The splash covering the thread: the study's welcome at the start, the
  // ending at the finish. Never the header, so the panel can always be closed.
  private overlay!: HTMLElement;
  private overlayBadge!: HTMLElement;
  private overlayTitle!: HTMLElement;
  private overlayText!: HTMLElement;
  private overlayActions!: HTMLElement;
  private overlayNote!: HTMLElement;
  /** The consent question itself, between the welcome and its buttons. */
  private overlayPrompt!: HTMLElement;
  /** Voice start screen: reads the study's welcome out. */
  private overlayListen!: HTMLButtonElement;
  private overlayLang!: HTMLElement;
  /** The voice start screen's study welcome: hero, time estimate, footer. */
  private overlayHero!: HTMLImageElement;
  private overlayTime!: HTMLElement;
  private overlayFooter!: HTMLElement;
  private langSelect!: HTMLSelectElement;
  // Chrome labelled in the interview's language, repainted on a switch.
  private titleEl!: HTMLElement;
  private closeBtn: HTMLButtonElement | null = null;

  /** The interview's language: the study's until the visitor (or
   *  config.language) picks another at the welcome. */
  private lang: Language = DEFAULT_LANGUAGE;
  /** The welcome bubble in the thread, so a re-issued welcome (language
   *  switch) replaces it rather than stacking a second one. */
  private welcomeBubble: HTMLElement | null = null;
  /** A language switch is in flight: whatever welcome lands meanwhile is
   *  shown with its buttons held, and the gate stays armed for the
   *  re-issued one. */
  private languageSwitching = false;

  private interviewId = "";
  private opened = false;
  private started = false;
  private starting = false;
  private done = false;
  private destroyed = false;
  private awaitingReply = false;
  private currentQuestion: QuestionView | null = null;
  // PII masking (src/pii.ts): on for a study whose branding says so.
  private pii = createMasker();
  /** The spec drawn for `currentQuestion` — what an interviewer bubble is
   *  trimmed against, so the thread never restates the choices below it. */
  private currentSpec: UISpec | null = null;
  private pendingRaw = "";                      // untrimmed deltas so far
  private spokenMessageIds = new Set<string>(); // dedupe SSE vs reply echo
  /** Survey the storage keys are scoped to: the configured id, else the one
   *  the bootstrap resolved from the publishable key. */
  private surveyKey = "";
  // The study's branding, fetched once per survey for its theme and language.
  private brandingFetch: { surveyId: string; promise: Promise<SurveyBranding | null> } | null = null;
  // The scheme painted: the study's default mode, else the visitor's device.
  private defaultMode = "system";
  private schemeQuery: MediaQueryList | null = null;
  private readonly onSchemeChange = () => this.applyScheme();
  // The study theme's stylesheet in the shadow root, once applied.
  private brandSheet: CSSStyleSheet | HTMLStyleElement | null = null;
  /** Snowball invite spoken as the closing turn, kept for the ending splash —
   *  the splash covers the thread the moment it appears. */
  private liveInvite = "";
  /** True from the moment an interview starts (or resumes mid-welcome) until
   *  the welcome turn has been answered on the splash. */
  private welcomeGate = false;
  private welcomeText = "";
  /** Voice: the start screen's answer to the welcome step (consent's Yes or
   *  No, or Continue), sent once the welcome turn is waiting for it. Until
   *  then the hands-free mic stays shut: the welcome is never answered aloud. */
  private voiceWelcomeReply = "";
  // Listen on the voice start screen: its playback, and a counter that drops
  // a load that was stopped before it finished.
  private listenCtx: AudioContext | null = null;
  private listenSource: AudioBufferSourceNode | null = null;
  private listenAudio: AudioBuffer | null = null;
  private listenGen = 0;
  private doneTimer: ReturnType<typeof setTimeout> | null = null;
  private disposeStream: (() => void) | null = null;
  private voice: VoiceSession | null = null;
  // Stepwise composition state (ranking / card sort).
  private picked: string[] = [];
  /** multi_select's ticked options as answer text ("" until enough are on);
   *  the composer's Send posts it when nothing is typed. */
  private panelAnswer: (() => string) | null = null;
  private cardAssignments: Record<string, string> = {};
  private cardCursor = 0;

  constructor(private config: DeuteroInterviewConfig) {
    if (!config.publishableKey && !config.sessionBootstrapToken) {
      throw new Error(
        "deutero widget: provide publishableKey or sessionBootstrapToken"
      );
    }
    this.api = new ApiClient(config.apiBaseUrl ?? window.location.origin);
    this.host = document.createElement("div");
    this.host.setAttribute("data-deutero-widget", "");
    this.shadow = this.host.attachShadow({ mode: "open" });
    this.applyTheme();
    this.applyScheme();
    this.watchScheme();
    this.injectStyles();
    this.render();
    (config.container ?? document.body).appendChild(this.host);
    if (config.openOnLoad || config.container) this.open();
    queueMicrotask(() => this.emitter.emit("ready", undefined));
    // Known survey: theme the widget now, before the visitor opens it.
    if (config.surveyId) void this.loadStudyBranding(config.surveyId);
  }

  on<K extends keyof WidgetEvents & string>(
    event: K,
    handler: (payload: WidgetEvents[K]) => void
  ): () => void {
    return this.emitter.on(event, handler);
  }

  // ── Language ────────────────────────────────────────────────────────────

  private t(key: string, vars?: Record<string, string | number>): string {
    return translate(this.lang.code, key, vars);
  }

  /** Adopt `language` for everything the widget paints itself. Unknown values
   *  are ignored (the current language stays). */
  private applyLanguage(language: string | null | undefined): void {
    const next = findLanguage(language);
    if (!next) return;
    const changed = next !== this.lang;
    this.lang = next;
    this.host.lang = next.code;
    this.paintChrome();
    if (this.langSelect) this.langSelect.value = next.name;
    if (changed) {
      if (this.currentSpec && !this.done) this.renderChoices(this.currentSpec);
      if (this.currentQuestion) this.progressEl.textContent = this.progressText(this.currentQuestion);
      this.emitter.emit("language", { language: next.name, code: next.code });
    }
  }

  /** Labels on the fixed chrome, in the current language. */
  private paintChrome(): void {
    const title = this.config.title ?? this.t("interview");
    this.titleEl.textContent = title;
    this.panel.setAttribute("aria-label", title);
    this.closeBtn?.setAttribute("aria-label", this.t("close_interview"));
    this.bubble?.setAttribute("aria-label", this.t("open_interview"));
    this.input.placeholder = this.t("type_answer");
    this.input.setAttribute("aria-label", this.t("your_answer"));
    this.sendBtn.setAttribute("aria-label", this.t("send"));
    this.micBtn?.setAttribute(
      "aria-label",
      this.t(this.voice?.mode === "vad" ? "mic_hands_free" : "hold_to_talk")
    );
    this.overlayLang.setAttribute("aria-label", this.t("language"));
    this.paintRecording();
  }

  /**
   * Switch the interview's language at the welcome. The workflow re-issues
   * the welcome in it (the study's stored translation when there is one) and
   * speaks it from then on; the new welcome arrives on the stream like the
   * first, so the splash repaints through the ordinary `welcomeGate` path.
   */
  async setLanguage(language: string): Promise<boolean> {
    const next = findLanguage(language);
    if (!next || !this.interviewId || this.done) return false;
    if (next === this.lang) return true;
    const previous = this.lang;
    this.languageSwitching = true;
    this.setGateButtonsDisabled(true);
    this.langSelect.disabled = true;
    this.applyLanguage(next.name);
    if (this.gated) this.welcomeGate = true;   // the re-issued welcome lands on the splash
    try {
      await this.api.setLanguage(this.interviewId, next.name);
      return true;
    } catch (err) {
      this.welcomeGate = false;
      this.applyLanguage(previous.name);
      if (!this.overlay.hidden && this.welcomeText) {
        this.languageSwitching = false;
        this.showWelcomeGate(this.welcomeText);
        this.setNote(this.t("language_error"), true);
      }
      this.emitter.emit("error", { detail: String(err) });
      return false;
    } finally {
      this.languageSwitching = false;
      this.langSelect.disabled = false;
      this.setGateButtonsDisabled(false);
    }
  }

  private setGateButtonsDisabled(disabled: boolean): void {
    this.overlayActions
      .querySelectorAll("button")
      .forEach((b) => { (b as HTMLButtonElement).disabled = disabled; });
  }

  private progressText(view: QuestionView): string {
    if (view.index < 0 || view.count <= 0) return "";
    return this.t("progress", { n: view.index + 1, total: view.count });
  }

  // ── Mounting ────────────────────────────────────────────────────────────

  private applyTheme(): void {
    for (const [key, value] of Object.entries(this.config.theme ?? {})) {
      const name = key.startsWith("--") ? key : `--dtro-${key}`;
      this.host.style.setProperty(name, value);
    }
  }

  /** The scheme the widget paints, on the host as `data-dtro-scheme`: the
   *  study's default mode when it sets one, else the visitor's device. */
  private applyScheme(): void {
    this.host.setAttribute("data-dtro-scheme", resolveScheme(this.defaultMode));
  }

  private watchScheme(): void {
    try {
      this.schemeQuery = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null;
      this.schemeQuery?.addEventListener?.("change", this.onSchemeChange);
    } catch {
      this.schemeQuery = null;
    }
  }

  /** The study's branding, fetched once per survey (theme and language both
   *  come from it). */
  private studyBranding(surveyId: string): Promise<SurveyBranding | null> {
    if (this.brandingFetch?.surveyId !== surveyId) {
      this.brandingFetch = { surveyId, promise: this.api.getBranding(surveyId) };
    }
    return this.brandingFetch.promise;
  }

  /** Whether this study masks PII, from its branding; starts loading the
   *  masking engine the first time it says yes. */
  private async piiReady(): Promise<boolean> {
    if (!this.surveyKey) return false;
    const branding = await this.studyBranding(this.surveyKey).catch(() => null);
    const on = !!branding?.pii_masking;
    if (on && !this.pii.enabled) loadPiiEngine().catch(() => undefined);
    this.pii.enabled = on;
    return on;
  }

  /** Style the widget with the study's brand theme (src/theme.ts), unless the
   *  integrator opted out. Best effort: without it the widget keeps its look. */
  private async loadStudyBranding(surveyId: string): Promise<void> {
    if (this.config.useStudyTheme === false) return;
    const theme = (await this.studyBranding(surveyId))?.widget_theme;
    if (!theme || this.destroyed) return;
    this.defaultMode = theme.default_mode || "system";
    this.applyScheme();
    this.applyBrandCss(brandThemeCss(theme));
    this.loadFonts(safeFontsHref(theme.fonts_href));
  }

  private applyBrandCss(css: string): void {
    if (!css || this.brandSheet) return;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      this.shadow.adoptedStyleSheets = [...this.shadow.adoptedStyleSheets, sheet];
      this.brandSheet = sheet;
    } catch {
      const style = document.createElement("style");
      style.textContent = css;
      this.shadow.appendChild(style);
      this.brandSheet = style;
    }
  }

  /** Fonts declared inside a shadow root are not loaded, so the theme's
   *  Google Fonts stylesheet goes in the page's <head> — once per URL. */
  private loadFonts(href: string | null): void {
    if (!href) return;
    const existing = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>("link[data-deutero-fonts]")
    ).some((l) => l.getAttribute("href") === href);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-deutero-fonts", "");
    document.head.appendChild(link);
  }

  private injectStyles(): void {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(WIDGET_CSS);
      this.shadow.adoptedStyleSheets = [sheet];
    } catch {
      const style = document.createElement("style");
      style.textContent = WIDGET_CSS;
      this.shadow.appendChild(style);
    }
  }

  private render(): void {
    const inline = !!this.config.container;
    this.root = el("div", `dtro-root ${inline ? "inline" : this.config.position ?? "bottom-right"}`);

    // Labels are painted by paintChrome() once everything exists, and again
    // whenever the interview's language changes.
    this.panel = el("div", "dtro-panel");
    this.panel.setAttribute("role", "dialog");

    const header = el("div", "dtro-header");
    this.titleEl = el("span", "dtro-title");
    this.progressEl = el("span", "dtro-progress");
    header.append(this.titleEl, this.progressEl);
    if (!inline) {
      this.closeBtn = el("button", "dtro-iconbtn");
      this.closeBtn.innerHTML = ICONS.close;
      this.closeBtn.addEventListener("click", () => this.close());
      header.append(this.closeBtn);
    }

    this.log = el("div", "dtro-log");
    this.log.setAttribute("role", "log");
    this.log.setAttribute("aria-live", "polite");

    this.choices = el("div", "dtro-choices");
    this.choices.addEventListener("scroll", this.syncClip, { passive: true });
    if (typeof ResizeObserver === "function") new ResizeObserver(this.syncClip).observe(this.choices);
    this.status = el("div", "dtro-status");
    this.status.setAttribute("aria-live", "polite");

    const inputRow = el("div", "dtro-inputrow");
    this.input = el("textarea");
    this.input.rows = 1;
    this.input.addEventListener("input", () => this.recordingActivity());
    this.input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" && !ev.shiftKey) {
        ev.preventDefault();
        void this.submitInput();
      }
    });
    this.sendBtn = el("button", "dtro-send");
    this.sendBtn.innerHTML = ICONS.send;
    this.sendBtn.addEventListener("click", () => void this.submitInput());
    inputRow.append(this.input, this.sendBtn);

    if (this.config.mode === "voice") {
      this.micBtn = el("button", "dtro-mic");
      this.micBtn.innerHTML = ICONS.mic;
      this.micBtn.setAttribute("aria-pressed", "false");
      this.micBtn.addEventListener("click", () => void this.toggleMic());
      inputRow.append(this.micBtn);
    }

    // The overlay is a sibling of the thread, not of the header: a splash must
    // never take the close button away from someone on a stranger's page.
    // Recording notice: hidden unless the study records the session (the
    // voice socket's `config` frame says). It sits above the thread and stays
    // up for the whole interview — the interviewer's audio is stored too, so
    // it is not a marker on the mic button.
    this.recBar = el("div", "dtro-recbar");
    this.recBar.setAttribute("role", "status");
    this.recBar.hidden = true;

    // Any click in the panel is a recording activity signal.
    this.panel.addEventListener("click", () => this.recordingActivity());
    const body = el("div", "dtro-body");
    body.append(this.recBar, this.log, this.choices, this.status, inputRow, this.buildOverlay());

    const powered = el("div", "dtro-powered", "Powered by Deutero");
    this.panel.append(header, body, powered);
    this.root.append(this.panel);

    if (!inline) {
      this.bubble = el("button", "dtro-bubble");
      this.bubble.innerHTML = ICONS.chat;
      this.bubble.setAttribute("aria-expanded", "false");
      this.bubble.addEventListener("click", () => this.toggle());
      this.root.append(this.bubble);
      this.panel.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape") this.close();
      });
    }
    this.shadow.appendChild(this.root);
    this.paintChrome();
  }

  /** The splash's own chrome. Both states fill the same elements: a welcome
   *  reads as text plus its buttons, an ending as badge, title and text. */
  private buildOverlay(): HTMLElement {
    this.overlay = el("div", "dtro-overlay");
    this.overlay.hidden = true;
    this.overlayBadge = el("div", "dtro-overlay__badge", "✓");
    this.overlayBadge.setAttribute("aria-hidden", "true");
    this.overlayBadge.hidden = true;
    this.overlayTitle = el("h2", "dtro-overlay__title");
    this.overlayTitle.hidden = true;
    this.overlayText = el("div", "dtro-overlay__text");
    this.overlayText.hidden = true;
    this.overlayPrompt = el("p", "dtro-overlay__prompt");
    this.overlayPrompt.hidden = true;
    this.overlayListen = el("button", "dtro-listen") as HTMLButtonElement;
    this.overlayListen.type = "button";
    this.overlayListen.hidden = true;
    this.overlayListen.addEventListener("click", () => void this.toggleListen());
    this.overlayActions = el("div", "dtro-overlay__actions");
    this.overlayActions.hidden = true;
    this.overlayNote = el("p", "dtro-overlay__note");
    this.overlayHero = el("img", "dtro-overlay__hero");
    this.overlayHero.alt = "";
    this.overlayHero.hidden = true;
    this.overlayTime = el("p", "dtro-overlay__time");
    this.overlayTime.hidden = true;
    this.overlayFooter = el("div", "dtro-overlay__footer dtro-rich");
    this.overlayFooter.hidden = true;

    // The visitor's language pick, shown with the welcome (never the ending).
    this.overlayLang = el("div", "dtro-overlay__lang");
    this.overlayLang.setAttribute("role", "group");
    this.overlayLang.hidden = true;
    this.langSelect = el("select");
    for (const l of LANGUAGES) {
      const opt = el("option", "", l.native);
      opt.value = l.name;
      opt.lang = l.code;
      this.langSelect.append(opt);
    }
    this.langSelect.value = this.lang.name;
    this.langSelect.addEventListener("change", () => void this.setLanguage(this.langSelect.value));
    this.overlayLang.append(this.langSelect);

    const inner = el("div", "dtro-overlay__inner");
    inner.append(
      this.overlayLang,
      this.overlayHero,
      this.overlayBadge,
      this.overlayTitle,
      this.overlayTime,
      this.overlayText,
      this.overlayListen,
      this.overlayPrompt,
      this.overlayActions,
      this.overlayNote,
      this.overlayFooter
    );
    this.overlay.append(inner);
    return this.overlay;
  }

  /** Clear the splash back to empty and either show or hide it. */
  private resetOverlay(visible: boolean): void {
    this.overlayBadge.hidden = true;
    this.overlayTitle.hidden = true;
    this.overlayTitle.textContent = "";
    this.overlayText.hidden = true;
    this.overlayText.textContent = "";
    this.overlayText.classList.remove("dtro-rich");
    this.overlayHero.hidden = true;
    this.overlayHero.removeAttribute("src");
    this.overlayTime.hidden = true;
    this.overlayTime.textContent = "";
    this.overlayFooter.hidden = true;
    this.overlayFooter.replaceChildren();
    this.overlayPrompt.hidden = true;
    this.overlayPrompt.textContent = "";
    this.stopListening();
    this.overlayListen.hidden = true;
    this.overlayLang.hidden = true;
    this.overlayActions.hidden = true;
    this.overlayActions.replaceChildren();
    this.setNote("");
    this.overlay.hidden = !visible;
  }

  /** The splash's status line. Progress reads muted; only a genuine failure
   *  turns it red, so a healthy start doesn't flash an alarming line. */
  private setNote(text: string, error = false): void {
    this.overlayNote.textContent = text;
    this.overlayNote.classList.toggle("error", error);
    this.overlayNote.hidden = !text;
  }

  // ── Open/close lifecycle ────────────────────────────────────────────────

  open(): void {
    if (this.destroyed || this.opened) return;
    this.opened = true;
    // Ruling T: nothing from while the panel was closed is recorded — the
    // next session (started by the next activity) can't reach back past now.
    this.rec?.recorder.barrier();
    this.connectMic();
    this.panel.classList.add("open");
    this.bubble?.setAttribute("aria-expanded", "true");
    this.emitter.emit("open", undefined);
    if (!this.started && !this.starting) void this.start();
    // While a splash is up the decision on it owns the focus, not the
    // composer sitting disabled-looking behind it.
    if (this.overlay.hidden) this.input.focus();
  }

  close(): void {
    if (!this.opened || this.config.container) return;
    this.opened = false;
    // Ruling T: with the panel closed nothing is recorded. The live session
    // ends, the mic is disconnected from the recorder, and activity is
    // ignored until the panel reopens.
    void this.rec?.recorder.end("idle");
    this.connectMic();
    this.panel.classList.remove("open");
    this.bubble?.setAttribute("aria-expanded", "false");
    this.emitter.emit("close", undefined);
  }

  toggle(): void {
    this.opened ? this.close() : this.open();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.doneTimer) clearTimeout(this.doneTimer);
    this.disposeStream?.();
    this.voice?.stop();
    this.teardownRecording();
    this.schemeQuery?.removeEventListener?.("change", this.onSchemeChange);
    this.host.remove();
    this.emitter.clear();
  }

  // ── Interview lifecycle ─────────────────────────────────────────────────

  private async start(): Promise<void> {
    const surveyId = this.config.surveyId ?? "";
    const persist = this.config.persistSession !== false;
    // Already finished this study in this browser? Re-show the ending instead
    // of starting an interview that a full study would only refuse — telling
    // the visitor who just finished that they were turned away.
    const completed = persist && surveyId ? loadCompletion(surveyId) : null;
    if (completed) {
      this.surveyKey = surveyId;
      this.started = true;
      this.done = true;
      // Nothing to linger over: the thread behind this belongs to a previous
      // visit, so the ending goes up straight away.
      this.finishUI(completed.reason, 0);
      return;
    }
    this.starting = true;
    // The splash is up from the first moment: it carries the connecting note
    // now and the study's own welcome in a second, so nothing generic flashes
    // past on the way to the thread.
    this.resetOverlay(true);
    this.setNote(this.t("starting"));
    try {
      const stored = persist && surveyId ? loadSession(surveyId) : null;
      // Voice: a fresh interview waits on a start screen, as /voice's does —
      // the study's welcome to read and one Start, then the call. A resumed
      // one goes straight back to it.
      let recording: RecordingStep = { declined: false, settled: false };
      if (this.config.mode === "voice" && !stored) {
        this.voiceWelcomeReply = await this.voiceStartScreen(surveyId);
        // The recording notice comes before anything that could open a mic —
        // and, with the survey known, before the interview exists, so
        // declining leaves nothing behind.
        if (!this.destroyed && surveyId) recording = await this.recordingNoticeFor(surveyId);
      }
      if (this.destroyed) return;
      if (recording.declined) {
        this.declineRecording();
        return;
      }
      const session = await this.api.bootstrap({
        publishable_key: this.config.publishableKey,
        signed_token: this.config.sessionBootstrapToken,
        survey_id: surveyId || undefined,
        metadata: this.config.metadata,
        resume_interview_id: stored?.interviewId,
        resume_session_token: stored?.token,
        mode: this.config.mode ?? "chat",
      });
      this.interviewId = session.interview_id;
      this.surveyKey = surveyId || session.survey.id;
      this.pii.bind(this.interviewId);
      void this.piiReady();   // warm the model before the first answer
      if (persist) saveSession(this.surveyKey, session);
      // Learnt only now when the integrator named no survey.
      if (!surveyId) void this.loadStudyBranding(this.surveyKey);
      this.started = true;
      this.emitter.emit("start", {
        interviewId: session.interview_id,
        resumed: session.resumed,
      });

      // A fresh interview starts at 0: the welcome was published at workflow
      // start and replays from there, so nothing needs nudging. A resumed one
      // subscribes at the snapshot's tip instead — see restoreTranscript.
      // Either way the splash stays up until the welcome lands on it.
      this.welcomeGate = this.gated;
      const snapshot = session.resumed ? await this.restoreTranscript() : null;
      if (this.done) return;   // the snapshot said this interview is over
      // A fresh interview is in the study's language: learn it before the
      // welcome lands, so the splash and its buttons are painted in it. (A
      // resumed one took its language from the snapshot.)
      if (!snapshot?.language) await this.loadSurveyLanguage();
      // The connecting note stays up while the welcome generates; the gate
      // (or a thread to drop into) is what clears it.
      if (!this.welcomeGate) this.dismissSplash();
      if (this.config.mode === "voice") {
        // Resumed still at the welcome step (the reply never went in): it is
        // answered on the start screen, as a fresh start's is.
        const atWelcome = snapshot?.phase === "welcome";
        if (atWelcome && !this.voiceWelcomeReply) {
          this.voiceWelcomeReply = await this.voiceStartScreen(this.surveyKey);
          if (this.destroyed) return;
          this.dismissSplash();
        } else if (snapshot && !atWelcome) {
          this.voiceWelcomeReply = "";
        }
        // Store the answer (or, for a resumed interview or one whose survey
        // wasn't named, ask now) before the socket's `config` frame reports
        // what is recorded. The mic only opens after this.
        const effective = await this.settleRecording(recording);
        if (!effective || this.destroyed) return;
        await this.setupRecording(effective);
        if (this.destroyed) return;
        await this.startVoice();
        // Joined at the snapshot's tip: its `awaiting_input` won't come again.
        if (atWelcome && snapshot?.awaiting_input) this.sendVoiceWelcomeReply();
      } else {
        this.subscribe(snapshot?.stream_offset ?? 0);
        // The integrator asked for a language: switch while still at the
        // welcome. Once past it the interview's language is fixed.
        const wanted = findLanguage(this.config.language);
        const atWelcome = !snapshot || snapshot.phase === "welcome";
        if (wanted && wanted !== this.lang && atWelcome) void this.setLanguage(wanted.name);
      }
    } catch (err) {
      this.setNote(this.t("start_failed"), true);
      this.emitter.emit("error", { detail: String(err) });
    } finally {
      this.starting = false;
    }
  }

  // ── Recording notice ────────────────────────────────────────────────────

  /** Step one, before the interview exists: the study's policy, and the
   *  notice when it records anything. A failed lookup reads as off — it never
   *  holds the interview up. `settled` — nothing left to ask or store. */
  private async recordingNoticeFor(surveyId: string): Promise<RecordingStep> {
    const policy = await this.api.getRecordingPolicy(surveyId);
    if (this.destroyed) return { declined: false, settled: true };
    const model = noticeModel(policy, {}, "voice");
    if (!model.needed) return { declined: false, settled: true };
    const answer = await this.askRecording(model);
    if (!answer.accepted) return { declined: true, settled: true };
    return { declined: false, settled: false, choice: answer.choice };
  }

  /** Step two, once the interview exists: store the answer given in step one,
   *  or (resumed, or no survey named) look the consent up and ask now.
   *  Resolves with what is recorded (policy and consent together), or null
   *  when the participant declined a required recording. Every failure along
   *  the way means "not recording" and the interview goes on. */
  private async settleRecording(step: RecordingStep): Promise<Effective | null> {
    const off: Effective = { audio: false, video: false };
    const asEffective = (e: Partial<Effective> | null | undefined): Effective => ({
      audio: !!e?.audio,
      video: !!e?.video,
    });
    if (step.settled) return off;      // the study records nothing
    let choice = step.choice;
    if (!choice) {
      const res = await this.api.getRecordingConsent(this.interviewId);
      if (!res.body) return off;       // network / 5xx / refused: no notice, no recording
      const model = noticeModel(res.body.policy, res.body.consent, "voice");
      if (!model.needed) return asEffective(res.body.effective);
      const answer = await this.askRecording(model);
      if (this.destroyed) return null;
      if (!answer.accepted) {
        this.declineRecording();
        return null;
      }
      choice = answer.choice;
    }
    const res = await this.api.postRecordingConsent(this.interviewId, choice);
    if (res.body) return asEffective(res.body.effective);
    // Another tab answered first: its answer stands.
    if (res.status === 409 && res.code === "already_decided") {
      return asEffective((await this.api.getRecordingConsent(this.interviewId)).body?.effective);
    }
    // The answer was not stored, so nothing will be recorded. Say so: the
    // participant was told this would be recorded.
    this.setRecordingBroken();
    return off;
  }

  /** Show the notice on the splash, in place of the welcome. */
  private async askRecording(model: NoticeModel): Promise<{ accepted: boolean; choice: Draft }> {
    this.resetOverlay(true);
    const answer = await mountRecordingNotice(
      this.overlayActions.parentElement as HTMLElement,
      model,
      (key) => this.t(key)
    );
    this.resetOverlay(true);
    this.setNote(this.t("starting"));
    return answer;
  }

  /** "I don't agree" to a required recording: the consent-declined ending,
   *  with the mic never opened. */
  private declineRecording(): void {
    this.done = true;
    this.finishUI("consent_declined", 0);
  }

  /** The study's language from its (unauthenticated) branding. Best effort:
   *  without it the widget stays English, as the gates do for an unknown
   *  survey language. */
  private async loadSurveyLanguage(): Promise<void> {
    if (!this.surveyKey) return;
    const branding = await this.studyBranding(this.surveyKey);
    this.applyLanguage(branding?.language);
  }

  /** Whether this widget holds the welcome turn behind a splash at all. Voice
   *  mode doesn't: there the welcome is spoken, and a button to agree to
   *  something the visitor hasn't heard yet is worse than no button. (Its
   *  start screen, voiceStartScreen, comes before the call, with Start only.) */
  private get gated(): boolean {
    return this.config.mode !== "voice";
  }

  /**
   * The voice start screen: the study's welcome (title, hero, time estimate,
   * message, footer — the same `welcome` /voice paints), a Listen button that
   * reads it out, and the welcome step's decision as buttons — consent's
   * Yes / No, or one Start. Resolves with the reply to send (the canonical
   * consent option, or Continue). The welcome is never answered aloud: in the
   * call it is shown in the thread, and speech starts with the first
   * question. A study without a welcome gets a line on what is about to
   * happen.
   */
  private async voiceStartScreen(surveyId: string): Promise<string> {
    const branding = surveyId ? await this.studyBranding(surveyId) : null;
    if (this.destroyed) return "";
    this.applyLanguage(branding?.language);
    const w: StudyWelcome = branding?.welcome ?? {};
    this.resetOverlay(true);
    if (w.hero_url) {
      this.overlayHero.src = w.hero_url;
      this.overlayHero.alt = w.hero_alt || "";
      this.overlayHero.hidden = false;
    }
    this.overlayTitle.textContent = w.title || "";
    this.overlayTitle.hidden = !w.title;
    this.overlayTime.textContent = w.time_estimate || "";
    this.overlayTime.hidden = !w.time_estimate;
    if (w.html) {
      setRichText(this.overlayText, w.html);
      this.overlayText.classList.add("dtro-rich");
    } else {
      this.overlayText.textContent = this.t("voice_intro");
    }
    this.overlayText.hidden = false;
    if (w.footer_html) {
      setRichText(this.overlayFooter, w.footer_html);
      this.overlayFooter.hidden = false;
    }
    if (w.text?.trim()) {
      this.listenSurvey = surveyId;
      this.setListenState("idle");
      this.overlayListen.hidden = false;
    }
    // Consent: Yes / No, the replies the workflow's gate resolves (relabelled
    // as the study likes). Without it, one Start whose reply is Continue.
    const consent = w.consent ?? null;
    this.overlayPrompt.textContent = consent?.question ?? "";
    this.overlayPrompt.hidden = !consent;
    const choices = consent
      ? [
          { label: w.consent_yes_label || consent.yes, reply: consent.yes },
          { label: w.consent_no_label || consent.no, reply: consent.no },
        ]
      : [{ label: w.continue_label || this.t("start_interview"), reply: this.t("continue") }];
    const buttons = choices.map(({ label }, i) =>
      el("button", i === 0 ? "dtro-btn" : "dtro-btn secondary", label) as HTMLButtonElement
    );
    this.overlayActions.append(...buttons);
    this.overlayActions.hidden = false;
    if (this.opened) buttons[0]!.focus();
    const reply = await new Promise<string>((resolve) => {
      buttons.forEach((btn, i) => {
        btn.addEventListener("click", () => {
          buttons.forEach((b) => { b.disabled = true; });
          resolve(choices[i]!.reply);
        }, { once: true });
      });
    });
    this.resetOverlay(true);
    this.setNote(this.t("starting"));
    return reply;
  }

  // The study whose welcome Listen reads. The server reads its own copy of
  // the message (POST /tts/welcome); the page never sends text to speak.
  private listenSurvey = "";

  private setListenState(state: "idle" | "loading" | "playing"): void {
    const playing = state === "playing";
    this.overlayListen.dataset.state = state;
    this.overlayListen.setAttribute("aria-pressed", String(playing));
    this.overlayListen.innerHTML = playing ? ICONS.stop : ICONS.speaker;
    this.overlayListen.append(
      el("span", "", this.t(playing || state === "loading" ? "listen_stop" : "listen"))
    );
  }

  private stopListening(): void {
    this.listenGen++;
    if (this.listenSource) {
      try { this.listenSource.stop(); } catch { /* already stopped */ }
      this.listenSource = null;
    }
    if (this.overlayListen?.dataset.state && this.overlayListen.dataset.state !== "idle") {
      this.setListenState("idle");
    }
  }

  private async toggleListen(): Promise<void> {
    if (this.overlayListen.dataset.state !== "idle") {
      this.stopListening();
      return;
    }
    // Inside the click, so the browser lets it play.
    const ctx = (this.listenCtx ??= new AudioContext());
    if (ctx.state === "suspended") void ctx.resume();
    const gen = ++this.listenGen;
    this.setListenState("loading");
    try {
      if (!this.listenAudio) {
        const wav = await this.api.welcomeSpeech(this.listenSurvey, this.lang.name);
        this.listenAudio = await ctx.decodeAudioData(wav);
      }
      if (gen !== this.listenGen) return;
      const src = ctx.createBufferSource();
      src.buffer = this.listenAudio;
      src.connect(ctx.destination);
      src.onended = () => {
        if (this.listenSource !== src) return;
        this.listenSource = null;
        this.setListenState("idle");
      };
      this.listenSource = src;
      this.setListenState("playing");
      src.start();
    } catch (err) {
      if (gen !== this.listenGen) return;
      this.setListenState("idle");
      this.setNote(this.t("listen_failed"), true);
      this.emitter.emit("error", { detail: String(err) });
    }
  }

  /** The start screen's answer goes in once the welcome waits for it; then
   *  hands-free listening may start. */
  private sendVoiceWelcomeReply(): void {
    const reply = this.voiceWelcomeReply;
    this.voiceWelcomeReply = "";
    if (!reply || this.done) return;
    void this.sendMessage(reply);
    const voice = this.voice;
    if (voice && voice.mode === "vad" && !voice.isRecording && !this.micMuted) {
      void this.openMic(voice);
    }
  }

  /** Lift the splash and hand the thread over. */
  private dismissSplash(): void {
    this.welcomeGate = false;
    this.resetOverlay(false);
    if (this.opened) this.input.focus();
  }

  /**
   * Hold the study's own welcome — and the consent decision when it requires
   * one — on the splash rather than as a bubble the visitor meets after a
   * generic panel has flashed past. The welcome is painted into the thread as
   * usual (it is a real interviewer turn, and a resume repaints it) while this
   * renders the same text with the gate's options as buttons. Answering lifts
   * the splash and sends the reply down the ordinary path, so the workflow's
   * consent handling is untouched — the buttons are just a nicer way to say
   * Yes.
   */
  private showWelcomeGate(text: string): void {
    this.welcomeText = text || this.welcomeText;
    // No welcome to show (a study that opens straight into questions): there
    // is nothing for a gate to ask, so get out of the visitor's way.
    if (!this.welcomeText.trim()) {
      this.dismissSplash();
      return;
    }
    // Mid-switch, the welcome showing now is about to be replaced: keep the
    // gate armed for the re-issued one.
    this.welcomeGate = this.languageSwitching;
    const view = this.currentQuestion;
    // Consent on: the gate publishes its own options (Yes/No) and the reply
    // has to be one of them. Consent off: any reply proceeds, so one Continue
    // — whose label is the reply.
    const options = view?.id === "consent" && view.options?.length
      ? view.options
      : null;

    this.resetOverlay(true);
    this.overlayLang.hidden = this.config.languagePicker === false;
    // The welcome spells the decision out in prose because most channels have
    // no buttons to offer. This splash does, so the instructions stop where
    // the buttons begin — the thread keeps the turn whole. The buttons still
    // need the question they answer, so it sits right above them.
    this.overlayText.textContent = options
      ? bubbleText(this.welcomeText, this.currentSpec, this.lang.code)
      : this.welcomeText;
    this.overlayText.hidden = false;
    this.overlayPrompt.textContent = options ? view?.text ?? "" : "";
    this.overlayPrompt.hidden = !this.overlayPrompt.textContent;

    (options ?? [this.t("continue")]).forEach((label, i) => {
      const btn = el("button", i === 0 ? "dtro-btn" : "dtro-btn secondary", label);
      btn.addEventListener("click", () => {
        this.overlayActions
          .querySelectorAll("button")
          .forEach((b) => { (b as HTMLButtonElement).disabled = true; });
        this.dismissSplash();
        void this.sendMessage(label);
      });
      this.overlayActions.append(btn);
    });
    this.overlayActions.hidden = false;
    if (this.languageSwitching) this.setGateButtonsDisabled(true);
    else (this.overlayActions.firstElementChild as HTMLElement | null)?.focus();
  }

  /**
   * Repaint a resumed interview from its snapshot, and report where the live
   * stream should be joined.
   *
   * Two things here are what keep a resumed interview from dying:
   *
   *  • The stream is joined at `stream_offset` — the tip — so the run's
   *    events aren't replayed on top of the transcript we just painted. Only
   *    then is repainting safe; an older API that omits the offset falls back
   *    to replaying from 0, which paints the thread itself.
   *  • The `state: awaiting_input` that parked this interview sits *behind*
   *    that tip and will never arrive again, so whether the visitor has the
   *    turn is decided from `awaiting_input` here, not from the stream.
   */
  private async restoreTranscript(): Promise<InterviewSnapshot | null> {
    let snapshot: InterviewSnapshot;
    try {
      snapshot = (await this.api.getState(this.interviewId)) as InterviewSnapshot;
    } catch {
      return null; // best effort: replay from 0 still paints the thread
    }
    this.applyLanguage(snapshot.language);
    const question = snapshot.current_question ?? null;
    if (question) this.onQuestion(question);

    if (typeof snapshot.stream_offset === "number") {
      const turns = snapshot.transcript ?? [];
      // A language switch leaves earlier welcomes in the transcript; only the
      // last one is the turn the visitor is answering.
      const lastWelcome = turns.map((t) => t.tool_name).lastIndexOf("welcome");
      turns.forEach((turn, i) => {
        if (turn.message_id) this.spokenMessageIds.add(turn.message_id);
        if (turn.tool_name === "welcome" && i !== lastWelcome) return;
        const interviewer = turn.role !== "participant";
        // Only the closing turn belongs to the question in play, so it is the
        // only one whose restated choices the live widget duplicates.
        const last = interviewer && i === turns.length - 1;
        const said = this.pii.reveal(turn.text);
        const node = this.addMessage(
          interviewer ? "interviewer" : "participant",
          last ? bubbleText(said, this.currentSpec, this.lang.code) : said,
          said
        );
        if (i === lastWelcome) this.welcomeBubble = node;
      });
    }

    if (snapshot.done) {
      this.finish(snapshot.terminal_reason ?? "");
      return snapshot;
    }

    // Parked on the welcome step, this resume is the same moment a fresh
    // start is: hold the splash rather than dropping the visitor into a
    // thread whose one turn is the question the splash is meant to ask.
    const atWelcome = this.gated && snapshot.phase === "welcome";
    this.welcomeGate = atWelcome;
    if (snapshot.awaiting_input === false) {
      // Mid-generation: the utterance is still coming down the stream, and
      // the live `awaiting_input` will raise the gate when it lands.
      this.showTyping("");
    } else if (atWelcome) {
      this.showWelcomeGate(welcomeTurnText(snapshot));
    }
    return snapshot;
  }

  private subscribe(fromOffset: number): void {
    this.disposeStream = this.api.streamEvents(
      this.interviewId,
      fromOffset,
      (event) => this.onStreamEvent(event),
      () => undefined
    );
  }

  private onStreamEvent(event: StreamEvent): void {
    switch (event.topic) {
      case "utterance": {
        const utt = event.data as UtteranceEvent;
        // The invite is spoken as a closing turn, but the ending panel covers
        // the thread moments later — keep it for the panel too.
        if (utt.tool_name === "snowball_sampling") this.liveInvite = utt.text ?? "";
        if (utt.tool_name === "welcome") this.welcomeText = utt.text ?? "";
        this.clearTyping();
        if (!this.spokenMessageIds.has(utt.message_id)) {
          this.spokenMessageIds.add(utt.message_id);
          const said = this.pii.reveal(utt.text);
          const node = this.addMessage(
            "interviewer",
            bubbleText(said, this.currentSpec, this.lang.code),
            said
          );
          if (utt.tool_name === "welcome") {
            // a language switch re-issues the welcome: the new one replaces the old
            if (this.welcomeBubble && this.welcomeBubble !== node) this.welcomeBubble.remove();
            this.welcomeBubble = node;
          }
        }
        break;
      }
      case "delta": {
        const delta = event.data as { text?: string };
        this.showTyping(delta.text ?? "");
        break;
      }
      case "retry":
        // The generator threw its draft away and is starting over.
        this.resetTyping();
        break;
      case "question": {
        const view = event.data as QuestionView;
        // Consent was answered on the voice start screen; the reply is on its way.
        if (view.id === "consent" && this.voiceWelcomeReply) {
          this.currentQuestion = view;
          break;
        }
        this.onQuestion(view);
        break;
      }
      case "abandon":
        this.clearTyping();
        break;
      case "state": {
        const state = event.data as StateEvent;
        if (state.state === "completed") {
          this.finish(state.detail ?? "");
        } else if (state.state === "awaiting_input" && this.voiceWelcomeReply) {
          this.sendVoiceWelcomeReply();
        } else if (state.state === "awaiting_input" && this.welcomeGate) {
          // The turn is the visitor's — but the welcome is answered on the
          // splash, not in the thread it has just painted behind it.
          this.showWelcomeGate(this.welcomeText);
        }
        break;
      }
    }
  }

  private onQuestion(view: QuestionView): void {
    this.currentQuestion = view;
    this.picked = [];
    this.cardAssignments = {};
    this.cardCursor = 0;
    const spec = buildUISpec(view);
    this.currentSpec = spec;
    // spec.progress is the server contract's English; paint our own.
    this.progressEl.textContent = this.progressText(view);
    this.renderChoices(spec);
    this.emitter.emit("question", { uispec: spec, view });
  }

  /** `reason` is the interview's terminal reason (`all_questions_asked`,
   *  `survey_full`, `quota_denied:*`, …), which decides whether this is an
   *  ending to be thanked for or a refusal to be explained. */
  private finish(reason = ""): void {
    if (this.done) return;
    this.done = true;
    // The interviewer's closing words land in the thread just before this, so
    // hold the splash back long enough for them to be read. A refusal has
    // nothing to linger over — say so straight away.
    this.finishUI(reason, isClosedReason(reason) ? 0 : DONE_SPLASH_DELAY_MS);
    if (this.surveyKey) {
      clearSession(this.surveyKey);
      // Records completions only — a refusal must not be remembered, or a
      // study that reopens stays closed for everyone who saw the denial.
      persistCompletion(this.surveyKey, reason);
    }
    this.finishRecording();
    this.emitter.emit("complete", undefined);
  }

  /** Close the composer down now, and raise the ending splash after `delay`. */
  private finishUI(reason: string, delayMs: number): void {
    this.clearTyping();
    this.welcomeGate = false;
    this.currentSpec = null;
    this.renderChoices(buildUISpec(null));
    this.setStatus("");
    this.input.disabled = true;
    this.sendBtn.disabled = true;
    this.micBtn?.setAttribute("disabled", "true");
    if (this.doneTimer) clearTimeout(this.doneTimer);
    if (delayMs > 0) {
      this.doneTimer = setTimeout(() => this.showDoneSplash(reason), delayMs);
    } else {
      this.showDoneSplash(reason);
    }
  }

  /** The ending: title, message, and the snowball invite when the study still
   *  wants peers. A refusal gets no ✓ — nobody should be congratulated for
   *  finishing an interview that never ran. */
  private showDoneSplash(reason: string): void {
    if (this.destroyed) return;
    this.doneTimer = null;
    const copy = doneCopy(reason, this.lang.code);
    this.resetOverlay(true);
    this.overlay.classList.add("done");
    this.overlayBadge.hidden = copy.closed;
    this.overlayTitle.textContent = copy.title;
    this.overlayTitle.hidden = false;
    this.overlayText.textContent = copy.text;
    this.overlayText.hidden = false;
    if (!copy.closed) void this.appendInvite();
  }

  /** The peer-recruitment invite, if the study still wants one. Whether it is
   *  shown at all is the server's call (feature on, capacity left): the live
   *  closing utterance while we watched the interview end, else a fresh
   *  branding fetch — the invite a browser remembered could long be stale. */
  private async appendInvite(): Promise<void> {
    let invite = this.liveInvite;
    if (!invite && this.surveyKey) {
      invite = (await this.api.getBranding(this.surveyKey))?.snowball_invite ?? "";
    }
    const { message, url } = splitInvite(invite);
    if (this.destroyed || (!message && !url)) return;

    const wrap = el("div", "dtro-overlay__invite");
    if (message) wrap.append(el("p", "", message));
    if (url) {
      const link = el("a", "dtro-overlay__link", url);
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      const copyBtn = el("button", "dtro-chip subtle", this.t("copy_link"));
      copyBtn.addEventListener("click", async () => {
        try {
          await navigator.clipboard.writeText(url);
          copyBtn.textContent = this.t("copied");
          setTimeout(() => { copyBtn.textContent = this.t("copy_link"); }, 2000);
        } catch {
          /* no clipboard permission — the link is right there */
        }
      });
      wrap.append(link, copyBtn);
    }
    this.overlayActions.append(wrap);
    this.overlayActions.hidden = false;
  }

  // ── Sending answers ─────────────────────────────────────────────────────

  async sendMessage(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || this.done || this.awaitingReply) return;
    this.addMessage("participant", trimmed);
    // The host page gets what the visitor typed: it is their own page.
    this.emitter.emit("message", { role: "participant", text: trimmed });
    this.choices.replaceChildren();
    const answering = this.currentQuestion;
    // Resolved: the next `question` event re-sets it. Until then no bubble is
    // trimmed against a question that has already been answered.
    this.currentSpec = null;
    this.awaitingReply = true;
    this.sendBtn.disabled = true;
    try {
      await this.piiReady();
      const out = await this.pii.mask(trimmed, answering);
      if (this.voice) {
        this.voice.sendText(out.text, crypto.randomUUID(), out.masked);
      } else {
        const reply = await this.api.sendMessage(
          this.interviewId,
          out.text,
          crypto.randomUUID(),
          out.masked
        );
        this.onReply(reply);
      }
    } catch (err) {
      // 409 = the workflow refused the answer because this interview is
      // already over (the 503 rollover case is retried inside the client).
      if (err instanceof ApiError && err.status === 409) {
        this.addSystem(this.t("conversation_ended"));
        this.finish();
      } else {
        this.setStatus(this.t("message_failed"));
      }
      this.emitter.emit("error", { detail: String(err) });
    } finally {
      if (!this.voice) {
        this.awaitingReply = false;
        this.sendBtn.disabled = this.done;
      }
    }
  }

  private onReply(reply: InterviewerReply): void {
    // Utterances also arrive via the stream; the reply echo covers the
    // no-stream (voice reply frame) and race cases. Dedupe on message_id.
    for (const utt of reply.utterances ?? []) {
      if (!this.spokenMessageIds.has(utt.message_id)) {
        this.spokenMessageIds.add(utt.message_id);
        this.addMessage("interviewer", this.pii.reveal(utt.text));
      }
    }
    if (reply.done) this.finish();
    if (this.voice) {
      this.awaitingReply = false;
      this.sendBtn.disabled = this.done;
    }
  }

  private async submitInput(): Promise<void> {
    // one Send for typed text and multi-select picks alike; typed text wins
    const picks = this.panelAnswer && this.choices.childElementCount ? this.panelAnswer() : "";
    const text = this.input.value.trim() || picks;
    this.input.value = "";
    await this.sendMessage(text);
    this.input.focus();
  }

  // ── The contact step (contact.ts) ──────────────────────────────────────

  /** The form, in the choices box. It posts to its own endpoint, so nothing
   *  typed into it reaches the transcript; typing an email or phone into the
   *  answer box works too (the interviewer reads it from the reply). */
  private renderContactForm(view: QuestionView): void {
    const submit = async (body: ContactValues & { declined: boolean }): Promise<ContactResult> => {
      if (this.done || this.awaitingReply || !this.interviewId) return { ok: false, code: "failed" };
      this.awaitingReply = true;
      this.sendBtn.disabled = true;
      const res = await this.api.submitContact(this.interviewId, body);
      this.awaitingReply = false;
      this.sendBtn.disabled = this.done;
      if (res.ok) {
        const text = this.t(body.declined ? "contact.declined" : "contact.shared");
        this.addMessage("participant", text);
        this.emitter.emit("message", { role: "participant", text });
        this.currentSpec = null;
        if (res.reply) this.onReply(res.reply);
      } else if (res.status === 409) {
        // the interview moved on (or ended) under the form
        this.setStatus(this.t("message_failed"));
      }
      return res;
    };
    const form = buildContactForm(view, (key) => this.t(key), {
      onSubmit: (values) => submit({ ...values, declined: false }),
      onDecline: () => submit({ declined: true }),
    });
    this.choices.append(form.el);
  }

  // ── UISpec rendering (answers compose to canonical plain text) ─────────

  private renderChoices(spec: UISpec): void {
    const box = this.choices;
    box.replaceChildren();
    this.panelAnswer = null;
    if (this.done) return;
    try {
      this.renderChoiceBody(spec, box);
    } finally {
      if (box.childElementCount) box.append(this.moreBar());
      box.scrollTop = 0;
      this.syncClip();
    }
  }

  /** While options remain below the fold of the (capped) choices box, a
   *  "Show more" bar pins to its foot, so nothing sits silently off-screen. */
  private moreBar(): HTMLElement {
    const wrap = el("div", "dtro-more");
    const btn = el("button", "", this.t("show_more"));
    btn.type = "button";
    btn.tabIndex = -1;   // keyboard users Tab or scroll instead
    btn.addEventListener("click", () => {
      const box = this.choices;
      box.scrollBy({ top: Math.max(60, box.clientHeight * 0.75), behavior: "smooth" });
    });
    wrap.append(btn);
    return wrap;
  }

  private syncClip = (): void => {
    const box = this.choices;
    const rest = box.scrollHeight - box.scrollTop - box.clientHeight;
    box.classList.toggle("clipped", box.childElementCount > 0 && rest > 4);
  };

  private renderChoiceBody(spec: UISpec, box: HTMLElement): void {

    if (isContactView(this.currentQuestion)) {
      this.renderContactForm(this.currentQuestion!);
      return;
    }

    const chip = (label: string, onPick: () => void, subtle = false) => {
      const btn = el("button", `dtro-chip${subtle ? " subtle" : ""}`, label);
      btn.addEventListener("click", onPick);
      box.append(btn);
      return btn;
    };
    const pnta = () => {
      if (spec.allow_pnta) {
        // PNTA_TEXT is what the server matches; only the label is translated
        chip(this.t("prefer_not"), () => void this.sendMessage(PNTA_TEXT), true);
      }
    };

    switch (spec.kind) {
      case "buttons":
        for (const b of spec.buttons) {
          chip(b.label, () => void this.sendMessage(b.value));
        }
        pnta();
        break;

      case "select": {
        const select = el("select");
        // spec.select.placeholder is the server contract's English
        const placeholder = spec.qtype === "scale" ? this.t("pick_number") : this.t("choose_one");
        select.append(new Option(placeholder, "", true, true));
        (select.options[0] as HTMLOptionElement).disabled = true;
        for (const opt of spec.select?.options ?? []) {
          select.append(new Option(opt, opt));
        }
        select.addEventListener("change", () => {
          if (select.value) void this.sendMessage(select.value);
        });
        box.append(select);
        pnta();
        break;
      }

      case "multi_select": {
        const selected = new Set<string>();
        const max = spec.max_select || spec.options.length;
        const min = Math.max(1, spec.min_select || 0);
        const count = el("div", "dtro-hint");
        count.setAttribute("aria-live", "polite");
        const sync = () => {
          count.textContent = selected.size
            ? this.t("n_selected", { n: selected.size })
            : this.t("tap_then_send");
        };
        this.panelAnswer = () =>
          selected.size >= min
            ? composeMultiSelect(spec.options.filter((o) => selected.has(o)))
            : "";
        box.append(count);
        sync();
        for (const opt of spec.options) {
          const btn = chip(opt, () => {
            if (selected.has(opt)) {
              selected.delete(opt);
              btn.classList.remove("selected");
            } else if (selected.size < max) {
              selected.add(opt);
              btn.classList.add("selected");
            }
            btn.setAttribute("aria-pressed", String(selected.has(opt)));
            sync();
          });
          btn.setAttribute("aria-pressed", "false");
        }
        pnta();
        break;
      }

      case "stepwise_ranking": {
        const remaining = spec.options.filter((o) => !this.picked.includes(o));
        const hint = el(
          "div",
          "dtro-hint",
          this.picked.length
            ? this.t("ranked_so_far", { list: this.picked.join(", "), n: this.picked.length + 1 })
            : this.t("pick_top")
        );
        box.append(hint);
        for (const opt of remaining) {
          chip(opt, () => {
            this.picked.push(opt);
            if (this.picked.length === spec.options.length) {
              void this.sendMessage(composeRanking(this.picked));
            } else {
              this.renderChoices(spec);
            }
          });
        }
        pnta();
        break;
      }

      case "stepwise_cardsort": {
        const card = spec.options[this.cardCursor];
        if (!card) break;
        box.append(
          el(
            "div",
            "dtro-hint",
            this.t("card_where", { card, n: this.cardCursor + 1, total: spec.options.length })
          )
        );
        for (const group of spec.groups) {
          chip(group, () => {
            this.cardAssignments[card] = group;
            this.cardCursor += 1;
            if (this.cardCursor >= spec.options.length) {
              void this.sendMessage(
                composeCardSortState(this.cardAssignments, spec.options, spec.groups)
              );
            } else {
              this.renderChoices(spec);
            }
          });
        }
        pnta();
        break;
      }

      case "images": {
        const gallery = el("div", "dtro-images");
        for (const image of spec.images) {
          const fig = el("figure");
          const img = el("img");
          img.src = image.url;
          img.alt = image.label || this.t("study_image");
          fig.append(img);
          if (image.label) fig.append(el("figcaption", "", image.label));
          gallery.append(fig);
        }
        this.log.append(gallery);
        this.scrollLog();
        break;
      }

      case "upload": {
        if (spec.hint) box.append(el("div", "dtro-hint", spec.hint));
        const picker = el("input") as unknown as HTMLInputElement;
        picker.type = "file";
        picker.accept = "image/*";
        picker.style.display = "none";
        picker.addEventListener("change", async () => {
          const file = picker.files?.[0];
          if (!file) return;
          this.setStatus(this.t("uploading"));
          try {
            const url = await this.api.upload(this.interviewId, file);
            this.setStatus("");
            await this.sendMessage(url);
          } catch (err) {
            this.setStatus(this.t("upload_failed"));
            this.emitter.emit("error", { detail: String(err) });
          }
        });
        chip(this.t("upload_image"), () => picker.click());
        box.append(picker);
        pnta();
        break;
      }
    }
  }

  // ── Voice mode ──────────────────────────────────────────────────────────

  private async startVoice(): Promise<void> {
    const rec = this.rec;
    const voice = new VoiceSession(this.api.voiceSocketUrl(this.interviewId), {
      onStreamEvent: (event) => this.onStreamEvent(event),
      onTranscript: (raw, answer) => {
        if (!raw) {
          this.setStatus(this.t("didnt_catch"));
          return;
        }
        this.addMessage("participant", answer);
        if (answer !== raw) {
          this.addSystem(this.t("heard", { raw, answer }));
        }
        this.emitter.emit("message", { role: "participant", text: answer });
        this.choices.replaceChildren();
      },
      onReply: (reply) => this.onReply(reply),
      onSpeaking: () => this.setStatus(this.t("speaking")),
      onIdle: () => this.setStatus(""),
      onRecordingChange: (recording) => {
        this.recordingActivity();
        this.micBtn?.classList.toggle("recording", recording);
        this.micBtn?.setAttribute("aria-pressed", String(recording));
        if (voice.mode === "vad") {
          // Hands-free: the mic stays on across turns; off means muted.
          this.setStatus(recording ? this.t("listening_hands_free") : "");
        } else {
          this.setStatus(this.t(recording ? "listening" : "transcribing"));
        }
      },
      onPartial: (text) => {
        this.recordingActivity();
        if (text) this.setStatus(text);
      },
      onTurn: (state) => {
        this.recordingActivity();
        if (state === "user_done") this.setStatus(this.t("transcribing"));
      },
      onTurnTaking: (mode) => {
        this.micBtn?.setAttribute(
          "aria-label",
          this.t(mode === "vad" ? "mic_hands_free" : "hold_to_talk")
        );
        // Hands-free means the server detects turns from a live mic: open it
        // now (this is what asks for permission), rather than waiting for a
        // tap on a button nobody is told to press.
        if (mode === "vad" && !voice.isRecording && !this.micMuted && !this.done
            && !this.voiceWelcomeReply) {
          void this.openMic(voice);
        }
      },
      onSessionRecording: (state) => this.showRecording(state),
      onError: (detail, fatal) => {
        this.setStatus(this.t(fatal ? "voice_unavailable" : "voice_hiccup"));
        this.emitter.emit("error", { detail });
      },
      // The recorder: each interviewer chunk is activity, the open mic feeds
      // its mic gate, mic frames are stamped on its capture clock (Ruling Q)
      // and the gateway's masking releases go to its hold-back.
      onAudio: () => this.recordingActivity(),
      onMicStream: (stream) => this.feedMic(stream),
      sampleClock: () => this.rec?.recorder.captureFrame() ?? null,
      clockRate: () => this.rec?.recorder.sampleRate ?? null,
      onRecClear: (start, end, bleep) => void this.rec?.recorder.release(start, end, bleep),
      // Word timings: a turn's arrive in page frames and land in the session
      // it was spoken in (or nowhere, if it straddles two).
      onTiming: (msg) => {
        const r = this.rec?.recorder.toSessionEvent(msg);
        r?.session.event(r.event);
      },
    }, {
      playback: rec ? { ctx: rec.ctx, output: rec.tap } : undefined,
      workletBase: this.api.baseUrl,
      playout: () => this.rec?.playout ?? null,
    });
    await voice.start();
    this.voice = voice;
  }

  private async toggleMic(): Promise<void> {
    if (!this.voice) return;
    if (this.voice.isRecording) {
      if (this.voice.mode === "vad") this.micMuted = true;
      this.voice.stopRecording();
    } else {
      this.micMuted = false;
      await this.openMic(this.voice);
    }
  }

  private async openMic(voice: VoiceSession): Promise<void> {
    try {
      await voice.startRecording();
    } catch (err) {
      this.setStatus(this.t("mic_unavailable"));
      this.emitter.emit("error", { detail: String(err) });
    }
  }

  // ── Log helpers ─────────────────────────────────────────────────────────

  /** `text` is what the thread shows (choices already trimmed off a
   *  structured turn); `raw` is what the server actually said, which is what
   *  the host page's `message` handler gets — an integrator logging the
   *  transcript wants the turn whole. */
  private addMessage(
    role: "interviewer" | "participant",
    text: string,
    raw = text
  ): HTMLElement | null {
    if (!text) return null;
    this.clearTyping();
    const node = el("div", `dtro-msg ${role}`, text);
    this.log.append(node);
    this.scrollLog();
    if (role === "interviewer") {
      this.emitter.emit("message", { role, text: raw });
    }
    return node;
  }

  private addSystem(text: string): void {
    this.log.append(el("div", "dtro-msg system", text));
    this.scrollLog();
  }

  /** Grow the in-flight interviewer bubble by one delta. The deltas are
   *  accumulated raw and re-trimmed on every paint, since the choices block
   *  the trim looks for can straddle any number of them. */
  private showTyping(deltaText: string): void {
    if (!this.typingEl) {
      this.typingEl = el("div", "dtro-msg interviewer pending");
      this.pendingRaw = "";
      this.log.append(this.typingEl);
    }
    this.pendingRaw += deltaText;
    // "…" holds the bubble open before the first delta lands — and for a
    // resumed interview that came back mid-generation, where the deltas
    // already spoken are behind the stream tip.
    const said = this.pii.reveal(this.pendingRaw, { streaming: true });
    this.typingEl.textContent = bubbleText(said, this.currentSpec, this.lang.code) || "…";
    this.scrollLog();
  }

  /** Same bubble, drafted from scratch (a `retry` on the stream). */
  private resetTyping(): void {
    this.pendingRaw = "";
    if (this.typingEl) this.typingEl.textContent = "";
  }

  private clearTyping(): void {
    this.typingEl?.remove();
    this.typingEl = null;
    this.pendingRaw = "";
  }

  private setStatus(text: string): void {
    this.status.textContent = text;
  }

  // ── Recording ───────────────────────────────────────────────────────────

  /** Start recording once the consent has settled, when audio is effective:
   *  one AudioContext for the interviewer's playback and the recorder, the
   *  TTS tap ahead of the output gain, and the mic gate. A browser that
   *  can't record is shown as broken — the participant was told they were
   *  being recorded. */
  private async setupRecording(effective: Effective): Promise<void> {
    if (!effective.audio || this.destroyed || this.rec) {
      this.paintRecording();
      return;
    }
    let ctx: AudioContext | null = null;
    try {
      ctx = typeof AudioContext === "function" ? new AudioContext() : null;
    } catch {
      ctx = null;
    }
    if (!ctx || !ctx.audioWorklet) {
      void ctx?.close().catch(() => undefined);
      this.setRecordingBroken();
      return;
    }
    const masking = await this.maskingForRecording();
    if (this.destroyed) {
      void ctx.close().catch(() => undefined);
      return;
    }
    const tap = ctx.createGain();
    const out = ctx.createGain();
    tap.connect(out).connect(ctx.destination);
    const micGate = ctx.createGain();
    const recorder: Recorder = new Recorder({
      apiBase: this.api.baseUrl,
      interviewId: this.interviewId,
      ctx,
      micInput: micGate,
      ttsTap: tap,
      masking,
      workletUrl: workletUrl(this.api.baseUrl, "recorder-worklet.js"),
      headers: (extra = {}) => {
        const token = this.api.sessionToken;
        return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
      },
      onError: (e) => {
        if (this.rec?.recorder !== recorder) return;
        console.warn("[deutero] recording", e);
        this.setRecordingBroken({ transient: true });
      },
      // Ruling U — 410: the session is no longer recording (finalized after
      // an upload lapse, or erased). The recorder has dropped it and reports
      // paused; the next activity opens seq + 1. Nothing is torn down: the
      // context is the interviewer's playback too.
      onGone: () => this.paintRecording(),
      onPaused: (paused) => {
        if (this.rec?.recorder !== recorder) return;
        this.recPaused = paused;
        // onPaused(false) comes once the server has created a session: a
        // recorder error before it has passed. 401/403/404 never get here.
        if (!paused && this.recBroken && this.recBrokenTransient) this.recBroken = false;
        this.paintRecording();
      },
    });
    const playout = new PlayoutLog<RecordingSession>({
      toFrame: (t) => recorder.frameAt(t),
      toSession: (f) => recorder.toSession(f),
      onFinish: (r: PlayoutResult<RecordingSession>) => {
        if (r.words) r.session.event(r.words);
        r.session.event(r.event);
      },
    });
    this.rec = { ctx, tap, out, micGate, recorder, playout };
    this.paintRecording();
    // The participant has just started the call: the first activity. (The
    // Recorder holds it until its worklet is attached, so no session exists
    // on the server — and "recording" isn't shown — before audio can flow.)
    this.recordingActivity();
  }

  /** Whether participant audio is held back for the server's PII release:
   *  the study's branding says so — and when it can't be read, yes. Raw
   *  audio from a masking study must never leave the browser. */
  private async maskingForRecording(): Promise<boolean> {
    const branding = this.surveyKey
      ? await this.studyBranding(this.surveyKey).catch(() => null)
      : null;
    return branding ? !!branding.pii_masking : true;
  }

  /** An activity signal: starts a recording session when there is none and
   *  resets the idle timer. Keeps the session token fresh for the uploads. */
  private recordingActivity(): void {
    const r = this.rec;
    if (!r || !this.opened) return;      // Ruling T: the panel is closed
    void r.ctx.resume?.().catch(() => undefined);
    r.recorder.activity();
    if (!this.tokenRefresh) {
      this.tokenRefresh = this.api
        .refreshIfNeeded()
        .catch(() => undefined)
        .finally(() => {
          this.tokenRefresh = null;
        });
    }
  }

  /** The open mic (or null once it closes) into the recorder's mic gate, so
   *  the participant is recorded only while the mic is on — and the panel is
   *  open (Ruling T). */
  private feedMic(stream: MediaStream | null): void {
    this.micStream = stream;
    this.connectMic();
  }

  /** (Re)connect the recorder's mic input to match the mic and the panel. */
  private connectMic(): void {
    try {
      this.micSource?.disconnect();
    } catch {
      /* already */
    }
    this.micSource = null;
    const r = this.rec;
    if (!this.micStream || !r || !this.opened) return;
    this.micSource = r.ctx.createMediaStreamSource(this.micStream);
    this.micSource.connect(r.micGate);
  }

  /** The interview completed: end the recording with "complete" once the
   *  closing line has played (at most RECORDING_FINISH_WAIT_MS). */
  private finishRecording(): void {
    const r = this.rec;
    if (!r || this.recFinishing) return;
    this.recFinishing = true;
    const started = Date.now();
    const tick = () => {
      if (this.rec !== r) return;
      if (this.voice?.isPlaying && Date.now() - started < RECORDING_FINISH_WAIT_MS) {
        setTimeout(tick, 250);
        return;
      }
      void r.recorder.end("complete");
    };
    tick();
  }

  /** Unmount: end the session, let the worklet go, close the context. */
  private teardownRecording(): void {
    this.feedMic(null);
    const r = this.rec;
    if (!r) return;
    this.rec = null;
    this.recPaused = true;
    void r.recorder
      .dispose("complete")
      .finally(() => r.ctx.close().catch(() => undefined));
  }

  /** `transient`: a recorder error that the next created session clears.
   *  Anything else (consent not stored, no AudioWorklet) stays broken. */
  private setRecordingBroken({ transient = false }: { transient?: boolean } = {}): void {
    this.recBrokenTransient = this.recBroken ? this.recBrokenTransient && transient : transient;
    this.recBroken = true;
    this.paintRecording();
  }

  /** What the socket's `config` says the study records. The bar follows the
   *  widget's own Recorder; this only catches a recording promised but not
   *  being made (no Recorder running), which the participant is owed. */
  private showRecording(state: SessionRecording): void {
    if (state.audio && !this.rec && !this.recBroken) this.setRecordingBroken();
  }

  /** Say whether this session is being recorded — recording while a session
   *  is live, paused while idle, and broken when it stopped working: a
   *  participant who was told they were being recorded is owed the correction
   *  as much as the notice. Hidden when nothing is recorded. */
  private paintRecording(): void {
    const bar = this.recBar;
    if (!bar) return;
    const paused = !this.recBroken && !!this.rec && this.recPaused;
    bar.hidden = !this.recBroken && !this.rec;
    bar.classList.toggle("broken", this.recBroken);
    bar.classList.toggle("paused", paused);
    bar.textContent = this.recBroken
      ? this.t("recording_broken")
      : !this.rec
        ? ""
        : paused
          ? this.t("rec.indicator.paused")
          : indicatorText({ audio: true, video: false }, (key) => this.t(key));
  }

  private scrollLog(): void {
    this.log.scrollTop = this.log.scrollHeight;
  }
}
